import React, { useState } from "react";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";

interface ProductItem {
  _id: string;
  type: string;
  date: string;
  sender: string;
  note: string;
  category: string;
}

interface ProductTabProps {
  datas: ProductItem[];
  hasMore: boolean;
  handleLoadMore: () => void;
  onFilter: (filters: any) => void;
  onReset: () => void;
}

const ProductFilter = ({ onFilter, onReset, companyColor }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    type: "",
    sender: "",
  });

  const handleChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const formatDateTimeForAPI = (
    dateString: string,
    isEndDate: boolean = false
  ) => {
    if (!dateString) return "";
    const time = isEndDate ? "23:59:59" : "00:00:00";
    return moment(dateString).format(`YYYY-MM-DD ${time}`);
  };

  const handleApply = () => {
    const formattedFilters = {
      ...filters,
      startDate: formatDateTimeForAPI(filters.startDate, false),
      endDate: formatDateTimeForAPI(filters.endDate, true),
    };
    onFilter(formattedFilters);
    setIsOpen(false);
  };

  const handleReset = () => {
    const resetFilters = {
      startDate: "",
      endDate: "",
      type: "",
      sender: "",
    };
    setFilters(resetFilters);
    onReset();
    setIsOpen(false);
  };

  return (
    <div className="mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-white rounded-lg shadow px-4 py-2 flex items-center gap-2 w-full justify-between hover:bg-gray-50 transition"
      >
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon="filter" className="text-gray-600" />
          <span className="font-medium text-gray-700">Filter</span>
        </div>
        <FontAwesomeIcon
          icon={isOpen ? "chevron-up" : "chevron-down"}
          className="text-gray-600"
        />
      </button>

      {isOpen && (
        <div className="bg-white rounded-lg shadow mt-2 p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tanggal
            </label>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleChange("startDate", e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleChange("endDate", e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipe
            </label>
            <select
              value={filters.type}
              onChange={(e) => handleChange("type", e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">Semua</option>
              <option value="MASUK">MASUK</option>
              <option value="KELUAR">KELUAR</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nama Pengirim
            </label>
            <input
              type="text"
              value={filters.sender}
              onChange={(e) => handleChange("sender", e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Masukkan nama pengirim"
            />
          </div>

          <div className="grid grid-cols-2 gap-2 pt-2">
            <button
              onClick={handleReset}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
            >
              Reset
            </button>
            <button
              onClick={handleApply}
              style={{ backgroundColor: companyColor || "#9333ea" }}
              className="px-4 py-2 text-white rounded-lg text-sm font-medium hover:opacity-90 transition"
            >
              Terapkan
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ProductTab: React.FC<ProductTabProps> = ({
  datas,
  hasMore,
  handleLoadMore,
  onFilter,
  onReset,
}) => {
  const navigate = useNavigate();
  const [companyProfile] = useState<any>(
    JSON.parse(localStorage.getItem("companyProfile") || "{}")
  );

  return (
    <div className="relative flex-1 overflow-y-auto p-4 space-y-4">
      <ProductFilter
        onFilter={onFilter}
        onReset={onReset}
        companyColor={companyProfile?.color}
      />

      {datas?.map((item) => (
        <div
          key={item._id}
          className="bg-white rounded-xl shadow p-4 flex justify-between items-start"
        >
          <div>
            <div className="flex items-center gap-2">
              <FontAwesomeIcon
                icon={
                  item?.type === "MASUK"
                    ? "arrow-right-to-bracket"
                    : "arrow-right-from-bracket"
                }
                className={
                  item?.type === "MASUK" ? "text-green-600" : "text-red-700"
                }
              />
              {item?.type === "MASUK" ? (
                <p className="text-sm text-green-700 font-medium">
                  {item?.type}
                </p>
              ) : (
                <p className="text-sm text-red-700 font-medium">{item?.type}</p>
              )}
            </div>
            <p className="text-sm text-gray-500">
              {moment(item?.date).format("DD MMM YYYY, HH:mm")}
            </p>
            <div className="mt-2">
              <p className="text-sm text-gray-500 flex items-center gap-2 font-light">
                <FontAwesomeIcon icon="user" className="text-gray-500" />
                Pengirim
              </p>
              <p className="font-semibold text-sm mt-1">{item.sender}</p>
            </div>
            <div className="mt-2">
              <p className="text-sm text-gray-500 flex items-center gap-1 font-light">
                <FontAwesomeIcon icon="file" className="text-gray-500" />
                Deskripsi
              </p>
              <p className="font-semibold text-sm mt-1">{item.note}</p>
            </div>
          </div>
        </div>
      ))}

      {hasMore ? (
        <div className="flex justify-center">
          <button
            onClick={handleLoadMore}
            className="bg-white rounded-xl shadow p-4 w-full hover:bg-gray-100 text-sm font-light transition"
          >
            Tampilkan lebih banyak
          </button>
        </div>
      ) : (
        <p className="text-center text-gray-500 text-sm font-light">
          Semua data telah ditampilkan
        </p>
      )}

      <button
        style={{ backgroundColor: companyProfile?.color || "#9333ea" }}
        onClick={() => navigate("/home/log-book-list/product/add")}
        className="fixed bottom-6 right-6 text-white rounded-full w-14 h-14 flex items-center justify-center text-2xl shadow-lg hover:opacity-90 transition"
        aria-label="Tambah barang"
      >
        +
      </button>
    </div>
  );
};

export default ProductTab;
